<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { destroySwiper } from '@/utils/site-utils';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { HeroLandingProps } from '@/types/components/HeroLanding';
import utils from '@/utils';
import 'swiper/css';

const props = defineProps<HeroLandingProps>();

let swiper: Swiper | null = null;
const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);

const slides = computed(() => props.fields?.slides ?? []);
const hasFieldValue = (field?: { value?: unknown }) => {
  if (typeof field?.value === 'string') {
    return field.value.replace(/<[^>]*>/g, '').trim().length > 0;
  }

  return Boolean(field?.value);
};
const hasSlideContent = (slide: (typeof slides.value)[number]) => {
  const fields = slide?.fields;
  return (
    hasFieldValue(fields?.title) ||
    hasFieldValue(fields?.body) ||
    hasFieldValue(fields?.button?.fields?.text)
  );
};
const layout = computed(() => {
  const value = props.fields?.layout?.fields?.phrase?.value;
  return value === 'center' ? 'center' : 'left';
});
const rootClass = computed(() => {
  const result = utils.overall.baseComClass(props.fields, 'c-hero-landing');
  result.push(`c-hero-landing--align-${layout.value}`);
  return result;
});

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});
const buildSwiper = () => {
  if (!swiperEl.value || !paginationEl.value) return;
  const slidesLen = slides.value.length;
  if (!slidesLen) return;

  destroySwiper(swiper, true, true);
  swiper = new Swiper(swiperEl.value, {
    slidesPerView: 1,
    observer: true,
    observeParents: true,
    modules: [Pagination],
    pagination: {
      el: paginationEl.value,
      clickable: true,
      bulletElement: 'span',
    },
  });
};

onMounted(() => {
  setTimeout(() => nextTick(() => buildSwiper()), 50);
});

watch(
  () => slides.value.length,
  (newLen, oldLen) => {
    if (newLen > 0 && newLen !== oldLen) {
      nextTick(() => buildSwiper());
    }
  }
);

onBeforeUnmount(() => {
  destroySwiper(swiper, true, true);
});
</script>

<template>
  <section :class="rootClass" :style="rootStyle" v-if="fields">
    <div class="swiper c-hero-landing__swiper" ref="swiperEl">
      <div class="swiper-wrapper">
        <div
          v-for="(slide, index) in slides"
          :key="slide.id || index"
          class="swiper-slide c-hero-landing__slide"
        >
          <div class="c-hero-landing__bg">
            <AdaptiveMedia
              v-if="slide.fields?.image"
              :field="slide.fields?.image"
              class="c-hero-landing__bg-img"
              :is-background="false"
              :lazy-load="index !== 0"
              :priority="index === 0"
              :options="{
                autoplay: true,
                loop: true,
                controls: false,
                preload: index === 0 ? 'auto' : 'metadata',
              }"
              :show-image-redius="false"
            />
          </div>
          <div v-if="hasSlideContent(slide)" class="c-hero-landing__overlay"></div>
          <div v-if="hasSlideContent(slide)" class="c-hero-landing__content">
            <div class="c-hero-landing__body">
              <h1 class="c-hero-landing__title" v-if="slide.fields?.title && index === 0">
                <strong v-html="slide.fields.title.value"></strong>
              </h1>
              <RichText
                v-if="slide.fields?.title && index !== 0"
                class="c-hero-landing__title"
                :field="slide.fields.title"
              />
              <RichText
                v-if="slide.fields?.body"
                class="c-hero-landing__desc"
                :field="slide.fields.body"
              />
              <div v-if="slide.fields?.button" class="c-hero-landing__actions">
                <SiteButton v-bind="slide.fields?.button" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="c-hero-landing__pagination swiper-pagination"
        ref="paginationEl"
        v-show="slides.length > 1"
      />
    </div>
  </section>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-hero-landing {
  position: relative;
  width: 100%;
  color: $white;
  overflow: hidden;

  &__swiper,
  &__slide,
  &__bg {
    width: 100%;
    height: auto;
  }

  &__slide {
    position: relative;
    overflow: visible;
  }

  &__bg {
    position: relative;
    z-index: 0;

    &-img {
      display: block;
      width: 100%;
      height: auto;

      > div,
      picture,
      .e-background-image,
      .e-background-image__img,
      .e-adaptive-media,
      .e-adaptive-media__image,
      .e-adaptive-media__video,
      .e-video-player,
      .background-image,
      video,
      img {
        width: 100%;
        height: auto;
      }

      .e-video-player,
      video {
        display: block;
        height: auto;
        object-fit: contain;
        object-position: center;
      }

      img {
        display: block;
        height: auto;
        object-fit: contain;
        object-position: center;
      }

      video {
        mix-blend-mode: normal;
      }
    }
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  &__content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 24px;
  }

  &__body {
    min-width: 0;
  }

  &__title {
    font-family: $font-family;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.18;
    letter-spacing: 0;
    color: $white;
    margin: 0 0 16px;

    p {
      margin: 0;
    }
  }

  &__desc {
    font-family: $font-family;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;

    p {
      margin: 0;
    }
  }

  &__actions {
    display: flex;
    margin-top: 26px;
  }

  &__pagination.swiper-pagination {
    position: absolute;
    bottom: 54px;
    left: 50%;
    right: auto;
    top: auto;
    transform: translateX(-50%);
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: auto;
  }

  &__swiper > &__pagination.swiper-pagination,
  &__swiper > &__pagination.swiper-pagination-bullets.swiper-pagination-horizontal {
    bottom: 54px;
    left: 50%;
    right: auto;
    top: auto;
    width: auto;
    transform: translateX(-50%);
  }

  .swiper-pagination {
    text-align: center;
    transition: 300ms opacity;
  }

  .swiper-pagination-bullet {
    display: inline-block;
    width: 32px;
    height: 4px;
    margin: 0 4px;
    border-radius: 0;
    background: rgba(255, 255, 255, 0.48);
    opacity: 1;
    cursor: pointer;
    transition:
      width 0.3s ease,
      background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }
  }

  .swiper-pagination-bullet-active {
    width: 36px;
    background: #6b9171;
  }

  @include tablet {
    &__content {
      align-items: center;
      padding: 0 0 0 86px;
    }

    &__title {
      font-size: 32px;
      line-height: 1.16;
      margin-bottom: 16px;
    }

    &__desc {
      font-size: 14px;
      line-height: 1.54;
      max-width: 860px;
    }

    &__swiper > &__pagination.swiper-pagination,
    &__swiper > &__pagination.swiper-pagination-bullets.swiper-pagination-horizontal {
      bottom: 55px;
    }
  }

  @include tablet-landscape {
    &__title {
      font-size: 44px;
    }

    &__desc {
      font-size: 16px;
    }
  }

  &--align-center {
    .c-hero-landing__content {
      align-items: center;
      text-align: center;
      padding: 0 28px;
    }

    .c-hero-landing__title {
      margin-bottom: 16px;
      font-size: 44px;
      line-height: 1.12;
    }

    .c-hero-landing__desc {
      margin: 0 auto;
    }

    .c-hero-landing__actions {
      justify-content: center;
    }

    @include tablet {
      .c-hero-landing__content {
        align-items: center;
        justify-content: center;
        padding: 0 84px;
      }

      .c-hero-landing__title {
        font-size: 32px;
      }
    }

    @include tablet-landscape {
      .c-hero-landing__title {
        font-size: 44px;
      }
    }
  }
}

.safari.ios .c-hero-landing {
  height: auto;
}
</style>
